import React, { useEffect, useState } from 'react';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import {
  CreateUpdateAttachmentDto,
  CreateUpdateContractDtoStatusEnum,
  SignaturePositionSchemaDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { PdfPlaceholder } from '../../../common/components/PdfViewer/ReactPdf';

import { ContractSignaturePdf } from './ContractSignaturePdf';

interface AttachmentViewerProps {
  attachmentId?: string;
  attachment: CreateUpdateAttachmentDto;
  showAttachmentViewer: boolean;
  withSignaturePosition: boolean;
  signaturePositions: SignaturePositionSchemaDto[];
  saveSignaturePositions: (
    positions: SignaturePositionSchemaDto[],
  ) => Promise<void>;
  status: CreateUpdateContractDtoStatusEnum;
  authorizedOwner: boolean;
  activeContract: boolean;
}

export const AttachmentViewer: React.FC<AttachmentViewerProps> = props => {
  const [fileContent, setFileContent] = useState<Blob>();
  const [pdfFile, setPdfFile] = useState<boolean>(false);
  const [isPdfEncrypted, setIsPdfEncrypted] = useState<boolean>(false);
  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);

  const pok = usePokCore();
  const notifications = useNotifications();

  useEffect(() => {
    let file: Blob | undefined;
    if (props.attachmentId && !props.attachment?.fileContent) {
      setIsFileLoading(true);
      pok.attachments
        .getFile(props.attachmentId)
        .then(response => {
          if (response.name.toLowerCase().endsWith('.pdf')) {
            setPdfFile(true);
            file = new Blob([Buffer.from(response.buffer as ArrayBuffer)]);
            setFileContent(file);
            pok.attachments
              .checkPdfIsEncrypted(props.attachmentId)
              .then(response => {
                setIsPdfEncrypted((response as unknown as string) === 'true');
              })
              .catch(error => {
                notifications.caughtError(error);
              });
          } else {
            setPdfFile(false);
            setFileContent(undefined);
          }
        })
        .catch(error => {
          notifications.caughtError(error);
        })
        .finally(() => setIsFileLoading(false));
    } else {
      if (
        props.attachment?.fileContent &&
        props.attachment?.name?.toLowerCase().endsWith('.pdf')
      ) {
        setPdfFile(true);
        file = new Blob([Buffer.from(props.attachment.fileContent, 'base64')]);
        setFileContent(file);
        pok.attachments
          .checkPdfIsEncrypted(undefined, {
            fileContent: props.attachment?.fileContent,
          })
          .then(response => {
            setIsPdfEncrypted((response as unknown as string) === 'true');
          })
          .catch(error => {
            notifications.caughtError(error);
          });
      } else {
        setPdfFile(false);
        setFileContent(undefined);
      }
    }
    return () => {
      if (file) {
        setFileContent(undefined);
      }
    };
  }, [
    notifications,
    pok.attachments,
    props.attachment?.fileContent,
    props.attachment?.name,
    props.attachmentId,
  ]);

  if (fileContent && pdfFile && props.showAttachmentViewer) {
    const signed =
      props.status === CreateUpdateContractDtoStatusEnum.Signed ||
      props.status === CreateUpdateContractDtoStatusEnum.Sent;
    const showPositions = props.withSignaturePosition && !signed;
    return (
      <ContractSignaturePdf
        file={fileContent}
        isPdfEncrypted={isPdfEncrypted}
        signaturePositions={props.signaturePositions}
        saveSignaturePositions={props.saveSignaturePositions}
        showPositions={showPositions}
        editable={
          showPositions &&
          props.activeContract &&
          (props.status !==
            CreateUpdateContractDtoStatusEnum.WaitingForSigning ||
            props.authorizedOwner)
        }
        status={props.status}
      />
    );
  }
  if (
    isFileLoading &&
    props.showAttachmentViewer &&
    props.attachment?.name?.toLowerCase().endsWith('.pdf')
  ) {
    return (
      <div className="pdf-viewer">
        <PdfPlaceholder />
      </div>
    );
  }
  return null;
};
